import { useId } from "react";

import type { WorkspaceFileWriteState } from "~/hooks/useWorkspaceFileWrite";
import {
  DialogWorkspaceFileTransferStatus,
  workspaceFileTransferPercent,
} from "../WorkspaceFileTransferStatus";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";

interface ProposedPlanSaveDialogProps {
  readonly open: boolean;
  readonly workspaceRoot: string | undefined;
  readonly savePath: string;
  readonly defaultFilename: string;
  readonly transferState: WorkspaceFileWriteState;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSavePathChange: (path: string) => void;
  readonly onCancel: () => void;
  readonly onSave: () => Promise<void>;
}

export function ProposedPlanSaveDialog({
  open,
  workspaceRoot,
  savePath,
  defaultFilename,
  transferState,
  onOpenChange,
  onSavePathChange,
  onCancel,
  onSave,
}: ProposedPlanSaveDialogProps) {
  const savePathInputId = useId();
  const isSaving = transferState.status === "saving";
  const percent = workspaceFileTransferPercent(transferState);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Save plan to project</DialogTitle>
          <DialogDescription>
            Enter a path relative to <code>{workspaceRoot ?? "the project"}</code>.
          </DialogDescription>
        </DialogHeader>
        <DialogPanel className="space-y-3">
          <label htmlFor={savePathInputId} className="grid gap-1.5">
            <span className="text-xs font-medium text-foreground">Project path</span>
            <Input
              id={savePathInputId}
              value={savePath}
              onChange={(event) => onSavePathChange(event.target.value)}
              placeholder={defaultFilename}
              spellCheck={false}
              disabled={isSaving}
            />
          </label>
          <DialogWorkspaceFileTransferStatus state={transferState} />
        </DialogPanel>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onCancel}>
            {isSaving ? "Cancel save" : "Cancel"}
          </Button>
          <Button size="sm" onClick={() => void onSave()} disabled={isSaving}>
            {isSaving ? `Saving ${percent}%` : "Save"}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
